import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BandzLogo from './BandzLogo';

/**
 * LOGO-HOME (2026-11-04) — the sidebar brand mark is a REAL link.
 *
 * Why it is not a plain `/home`: `/home` is the band GRID (pick a band, or
 * create one) — a choice screen, not a working screen. The everyday click on
 * the logo means "take me back to where the work happens", which is the
 * BAND LANDING (`/band/:slug/home`): it redirects to the Day-of page when a
 * show of that band is dated TODAY, and to the Route map otherwise (Atlas,
 * which opens on ALL bands when the account can see several).
 *
 * Fallbacks (`sidebarHomeHref`), never a guess:
 *   - a band is open                → `${base}/home` (contextual, above);
 *   - no band open, but the account
 *     has bands                     → `/tour/atlas` — the operational home is
 *                                     the multi-band Route map, not the grid;
 *   - no band at all                → `/home` — the grid, which is where the
 *                                     “Create a band” state lives.
 *
 * The click also has to close the mobile drawer: below 900px the sidebar IS an
 * off-canvas drawer (`menuOpen` in AppLayout), and without `onNavigate` the
 * drawer would stay open on top of the page the user just asked for.
 */
export function sidebarHomeHref(bandBase: string, bandCount: number): string {
  if (bandBase) return `${bandBase}/home`;
  return bandCount > 0 ? '/tour/atlas' : '/home';
}

interface SidebarBrandLinkProps {
  /** Destination computed by `sidebarHomeHref`. */
  to: string;
  /** Called on click — AppLayout passes `() => setMenuOpen(false)`. */
  onNavigate?: () => void;
}

export default function SidebarBrandLink({ to, onNavigate }: SidebarBrandLinkProps) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      className="sidebar-brand-link"
      data-sidebar-logo-link
      aria-label={t('tabs.home')}
      title={t('tabs.home')}
      onClick={onNavigate}
    >
      <BandzLogo className="sidebar-brand-logo" />
    </Link>
  );
}
